import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { LaeProductivityQuery } from '@/lib/types';

/**
 * The LAE Productivity (Schools mode) data layer. Ports the two Schools hooks of LAE
 * `features/productivity/queries.js` (`useProductivitySchools`, `useProductivitySchool`) onto
 * EMERGE's fetching pattern (copied from `lae/use-lae-data.ts` `useLaeSummary`).
 *
 * - `useEffect` + `AbortController`; a `reloadToken` forces a refetch.
 * - The last data stays on screen while a new key loads (`refreshing`); `loading` is only true
 *   when there is nothing to show yet. Loading is derived from request keys, never set inside
 *   the effect, and a result settles only when its key matches the current request.
 * - Params are keyed on a STABLE serialisation (null / undefined / '' dropped, keys sorted), so a
 *   referentially new but equal object never refetches. LAE `clean()` does the same for its
 *   cache key; here it also keeps the empty string (a 422 upstream) off the wire.
 *
 * Deliberate differences from LAE:
 * - No react-query cache, `staleTime` or `retry`; the server's 15-minute cache stands in.
 * - Null params = idle (LAE's `enabled: false`): nothing is fetched, data is null.
 * - The school hook keeps its last data only for the SAME school; opening another school never
 *   shows the previous school's figures under the new name.
 * - The previous window (`prevParams`) uses the same list hook. Its error is reported, never
 *   thrown; the caller treats an error or null data as "no deltas".
 */

type SchoolsResult = Awaited<ReturnType<typeof api.productInsightsLaeProductivitySchools>>;
type SchoolResult = Awaited<ReturnType<typeof api.productInsightsLaeProductivitySchool>>;

export interface LaeProductivityState<D, M> {
  data: D | null;
  meta: M | null;
  loading: boolean;
  refreshing: boolean;
  error: unknown | null;
}

/** Drops null / undefined / '' and sorts keys: equal params always give the same string. */
function paramsKeyOf(params: LaeProductivityQuery): string {
  const entries = Object.entries(params as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(Object.fromEntries(entries));
}

interface Settled<R> {
  key: string;
  result: R | null;
  error: unknown | null;
}

interface Kept<R> {
  /** Which identity the data belongs to (the school id; a constant for the list). */
  identity: string;
  result: R;
}

/**
 * Shared engine. `identity` null = idle. `paramsJson` is the stable serialisation; the effect
 * parses it back so it depends on strings only.
 */
function useKeyedFetch<R extends { data: unknown; meta: unknown }>(
  identity: string | null,
  paramsJson: string | null,
  reloadToken: number,
  fetcher: (identity: string, query: LaeProductivityQuery, signal: AbortSignal) => Promise<R>,
): LaeProductivityState<R['data'], R['meta']> {
  const requestKey =
    identity === null || paramsJson === null ? null : `${identity}|${paramsJson}#${reloadToken}`;
  const [kept, setKept] = useState<Kept<R> | null>(null);
  const [settled, setSettled] = useState<Settled<R> | null>(null);

  useEffect(() => {
    if (requestKey === null || identity === null || paramsJson === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const query = JSON.parse(paramsJson) as LaeProductivityQuery;
    fetcher(identity, query, signal)
      .then((result) => {
        if (signal.aborted) return;
        setKept({ identity, result });
        setSettled({ key: requestKey, result, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setSettled({ key: requestKey, result: null, error: err });
      });
    return () => controller.abort();
    // `fetcher` is a module-level constant per hook; the key carries everything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  if (requestKey === null) {
    return { data: null, meta: null, loading: false, refreshing: false, error: null };
  }
  const shown = kept !== null && kept.identity === identity ? kept.result : null;
  const current = settled !== null && settled.key === requestKey ? settled : null;
  const isSettled = current !== null;
  return {
    data: shown === null ? null : shown.data,
    meta: shown === null ? null : shown.meta,
    loading: shown === null && !isSettled,
    refreshing: shown !== null && !isSettled,
    error: current === null ? null : current.error,
  };
}

const LIST_IDENTITY = 'schools';

const fetchSchools = (_identity: string, query: LaeProductivityQuery, signal: AbortSignal) =>
  api.productInsightsLaeProductivitySchools(query, signal);

const fetchSchool = (schoolId: string, query: LaeProductivityQuery, signal: AbortSignal) => {
  // The detail route takes no `breakdown` (it is a list-only param).
  const { breakdown: _breakdown, ...rest } = query;
  return api.productInsightsLaeProductivitySchool(schoolId, rest, signal);
};

/**
 * `GET /product-insights/lae/productivity/schools`. Used for both the current window
 * (`apiParams`) and the previous one (`prevParams`). Null params = idle.
 */
export function useLaeProductivitySchools(
  params: LaeProductivityQuery | null,
  reloadToken: number,
): LaeProductivityState<SchoolsResult['data'], SchoolsResult['meta']> {
  const paramsJson = params === null ? null : paramsKeyOf(params);
  return useKeyedFetch<SchoolsResult>(
    paramsJson === null ? null : LIST_IDENTITY,
    paramsJson,
    reloadToken,
    fetchSchools,
  );
}

/**
 * `GET /product-insights/lae/productivity/schools/:schoolId`. A null or empty `schoolId`
 * (no modal open) is idle, as LAE's `enabled: !!schoolId`.
 */
export function useLaeProductivitySchool(
  schoolId: string | null,
  params: LaeProductivityQuery,
  reloadToken: number,
): LaeProductivityState<SchoolResult['data'], SchoolResult['meta']> {
  const identity = schoolId === null || schoolId === '' ? null : schoolId;
  return useKeyedFetch<SchoolResult>(identity, paramsKeyOf(params), reloadToken, fetchSchool);
}
